import { sellDeliveryNoteStatusInversed } from '@teinor/erp/types/company/order/sell/sellDeliveryNote'
import { SellCreateWorkSheetButton, SellDownloadButtonCSV, SellDownloadButtonPDF, SellDownloadPdfTemplateButton, SellDownloadTags, SellPdfPreview, SellViewWorkSheetButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout/SellButtons'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { SellDeliveryNoteDuplicateButton } from '../../../SellDeliveryNButtons'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { useMemo } from 'react'
import { getAllWorkSheetGroupStatuses } from '../../../../../../../../../../../utils/reducers/getters'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import { OrderDivideIntoChaptersButton, OrderProductTypesButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'

const SellDeliveryNoteCreateNoPermissionButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useSellDeliveryNoteCreateNoPermissionButtonsLogic, ...rest }} className='mr-8' />
    )
}

const useSellDeliveryNoteCreateNoPermissionButtonsLogic = ({ data, onSubmit, isReadOnly }: OrderStepButtonsBaseComponentProps) => {
    const {t} = useTwinTranslation()
    const name = 'sell_delivery_note_create_no_permission_step_buttons'
    const workSheets = getAllWorkSheetGroupStatuses()
    const Order = data?.header
    const showButtonByPricesPermission = !Order?.hidePrices
    const showButtonByStep = Order?.status !== sellDeliveryNoteStatusInversed['creating']
    const showCreateWorkSheetButton = showButtonByStep && !Order?.WorkSheetId && !!workSheets?.WorkSheetActiveStatusGroups?.length

    const defaultButtons = [{ "id": "previewSellDeliveryNote" }, { "id": "downloadSellDeliveryNote" }, { "id": "duplicateSell" }, { "id": "createWorkSheet" }, { "id": "viewWorkSheet" }]

    const sellDeliveryNoteDuplicateText = t('duplicateDeliveryNote', 'Duplicar albarán')
    const sellDeliveryNoteDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const sellDeliveryNoteDownloadTagsText = t('printTags', 'Imprimir etiquetas')
    const sellDeliveryNoteViewWorkSheetText = t('goToWorkSheet', 'Ir a la hoja de trabajo')
    const sellDeliveryNoteCreateWorkSheetText = t('createWorkSheet', 'Crear hoja de trabajo')
    const sellDeliveryNotePreviewPDFText = t('previewPdf', 'Previsualizar PDF')
    const sellDeliveryNoteDownloadPdfTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const sellDeliveryNoteDownloadPdfText = t('downloadPdf', 'Descargar PDF')
    const sellDeliveryNoteDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const sellDeliveryNoteProductTypesText = t('valueProductTypes', 'Valor tipos de producto')

    
    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateSell: {
                component: SellDeliveryNoteDuplicateButton,
                showButton: showButtonByPricesPermission,
                extraProps: {
                    title: sellDeliveryNoteDuplicateText,
                    id: 'duplicateSell',
                    allRowData: data,
                    key:'duplicateButton'
                }
            },
            downloadSellCsv: {
                component: SellDownloadButtonCSV,
                showButton: showButtonByPricesPermission,
                extraProps: {
                    title: sellDeliveryNoteDownloadCSVText,
                    id: 'downloadSellCsv',
                    Order,
                    typeTemplate:'sellDeliveryNote',
                }
            },
            downloadSellTag: {
                component: SellDownloadTags,
                showButton: showButtonByPricesPermission,
                extraProps: {
                    title: sellDeliveryNoteDownloadTagsText,
                    id: 'downloadSellTag',
                    Order,
                    typeTemplate:'sellDeliveryNote',
                }
            },
            viewWorkSheet: {
                component: SellViewWorkSheetButton,
                showButton: Order?.status === sellDeliveryNoteStatusInversed['creating'] && Order?.WorkSheetId,
                extraProps: {
                    title: sellDeliveryNoteViewWorkSheetText,
                    id: 'viewWorkSheet',
                    WorkSheetId: Order?.WorkSheetId
                }
            },
            createWorkSheet: {
                component: SellCreateWorkSheetButton,
                showButton: showCreateWorkSheetButton,
                extraProps: {
                    title: sellDeliveryNoteCreateWorkSheetText,
                    id: 'createWorkSheet',
                    Order,
                    createURL: '/api/app/order/sell/workSheet/createWorkSheetBySellDeliveryNote',
                    orderField: 'SellDeliveryNoteId',
                    completeData: data 
                }
            },
            previewSellDeliveryNote: {
                component: SellPdfPreview,
                showButton: showButtonByPricesPermission,
                extraProps: {
                    title: sellDeliveryNotePreviewPDFText,
                    id: 'previewSellDeliveryNote',
                    Order,
                    typeTemplate:'sellDeliveryNote',
                }
            },
            downLoadSellDeliveryNoteTemplate: {
                component: SellDownloadPdfTemplateButton,
                showButton: showButtonByPricesPermission,
                extraProps: {
                    title: sellDeliveryNoteDownloadPdfTemplateText,
                    id: 'downLoadSellDeliveryNoteTemplate',
                    Order,
                    key:'downloadSellDeliveryNotePDFTemplate',
                    typeTemplate:'sellDeliveryNote',
                }
            },
            downloadSellDeliveryNote: {
                component: SellDownloadButtonPDF,
                showButton: showButtonByPricesPermission,
                extraProps: {
                    title: sellDeliveryNoteDownloadPdfText,
                    id: 'downloadSellDeliveryNote',
                    Order,
                    key:'downloadSellDeliveryNote',
                    typeTemplate:'sellDeliveryNote',
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: sellDeliveryNoteDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    onSubmit,
                    isReadOnly,
                }
            },
            sellProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: sellDeliveryNoteProductTypesText,
                    id: 'sellProductTypes',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
        }
    }, [Order, data, onSubmit, sellDeliveryNoteDownloadCSVText, sellDeliveryNoteDownloadPdfTemplateText, sellDeliveryNoteDownloadPdfText, sellDeliveryNoteDownloadTagsText, sellDeliveryNoteDuplicateText, sellDeliveryNotePreviewPDFText, sellDeliveryNoteViewWorkSheetText, sellDeliveryNoteDivideIntoChaptersText, isReadOnly, sellDeliveryNoteCreateWorkSheetText, showButtonByPricesPermission, showCreateWorkSheetButton, sellDeliveryNoteProductTypesText])

    return { name, ButtonComponents, defaultButtons }
}

export const SellDeliveryNoteCreateNoPermissionButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useSellDeliveryNoteCreateNoPermissionButtonsLogic, ...rest }} className='mr-8' />
    )
}

export default SellDeliveryNoteCreateNoPermissionButtons